import { create } from 'zustand';

type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeState {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  cycleMode: () => void;
  initTheme: () => void;
}

const STORAGE_KEY = 'blast-crm-theme';

const cycleOrder: ThemeMode[] = ['light', 'dark', 'system'];

function getSystemPrefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function applyDarkClass(isDark: boolean): void {
  if (isDark) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

function applyMode(mode: ThemeMode): void {
  if (mode === 'system') {
    applyDarkClass(getSystemPrefersDark());
  } else {
    applyDarkClass(mode === 'dark');
  }
}

function getStoredMode(): ThemeMode {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored;
  }
  return 'light';
}

let mediaQueryListener: ((e: MediaQueryListEvent) => void) | null = null;

function setupSystemListener(mode: ThemeMode): void {
  const mq = window.matchMedia('(prefers-color-scheme: dark)');

  // Remove previous listener
  if (mediaQueryListener) {
    mq.removeEventListener('change', mediaQueryListener);
    mediaQueryListener = null;
  }

  // Only listen when in system mode
  if (mode === 'system') {
    mediaQueryListener = (e: MediaQueryListEvent) => {
      applyDarkClass(e.matches);
    };
    mq.addEventListener('change', mediaQueryListener);
  }
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  mode: 'light',

  setMode: (mode: ThemeMode) => {
    localStorage.setItem(STORAGE_KEY, mode);
    applyMode(mode);
    setupSystemListener(mode);
    set({ mode });
  },

  cycleMode: () => {
    const current = get().mode;
    const currentIndex = cycleOrder.indexOf(current);
    const nextMode = cycleOrder[(currentIndex + 1) % cycleOrder.length];
    get().setMode(nextMode);
  },

  initTheme: () => {
    const mode = getStoredMode();
    applyMode(mode);
    setupSystemListener(mode);
    set({ mode });
  },
}));
